import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { calculateNpsScoresByLabel } from "../../../utils/calculateNpsScore";
import { LoadingOutlined } from "@ant-design/icons";
import NpsOverTimeScoreChart from "./NpsOverTimeScoreChart";
import { formatDateToUtc } from "../../../utils/formatDate";
import { debounce } from "lodash";

const getMetadataFields = (metadata) =>
    metadata.filter((el) => el.type === "number" || el.type === "score");


const getMetadataOptions = (metadataFields) =>
    metadataFields.map((field) => ({
        label: field.name,
        value: field.name,
        values: field.values,
        type: field.type,
    }));

const NpsOverTime = (props) => {
    const {
        id,
        selectedDateName,
        interval,
        metaDataQu,
        selectedFieldDriverName,
        smoothLine,
        filters,
    } = props;
    const metadataFields = useMemo(
        () =>
            getMetadataFields(
                metaDataQu.metaData.filter((value) => value.values && value.values.length > 0),
            ),
        [metaDataQu.metaData],
    );
    const [loading, setLoading] = useState(false);
    const [npsData, setNpsData] = useState([]);
    const metadataOptions = useMemo(() => getMetadataOptions(metadataFields), [metadataFields]);

    const selectedOption = useMemo(
        () => metadataOptions.find((option) => option.label === selectedFieldDriverName) || metadataOptions[0],
        [selectedFieldDriverName, metadataOptions],
    );
    const conceptFilter = useMemo(
        () => JSON.stringify(getConceptFilter(props)),
        [props],
    );

    const [getVolume] = useGetConceptsFiltersMutation();

    const fetchAllLinesData = useCallback(async () => {
        setLoading(true);
        if (!selectedOption) return;

        const batchSize = 10;
        const batches = [];

        for (let i = 0; i < selectedOption.values.length; i += batchSize) {
            batches.push(selectedOption.values.slice(i, i + batchSize));
        }

        try {
            let combinedData = [];

            for (const batch of batches) {
                const batchPromises = batch.map((value) => {
                    const filterScore = selectedOption.type === "number"
                        ? [
                            {
                                name: selectedOption.value,
                                minimum: Number(value.value),
                                maximum: Number(value.value),
                            },
                        ]
                        : [
                            {
                                name: selectedOption.value,
                                values: [value.value],
                            },
                        ];


                    return getVolume({
                        id,
                        concept_selector: JSON.parse(conceptFilter),
                        filter: [...filterScore, ...filters],
                        breakdowns: [{ name: selectedDateName, interval }],
                    }).then((response) => ({
                        value: value.value,
                        data: response.data,
                    }));
                });

                const results = await Promise.all(batchPromises);

                const batchCombinedData = results.map((result) => ({
                    value: result.value,
                    relatedData: result.data?.breakdowns[0]?.buckets.map((bucket) => ({
                        filter_count: bucket.filter_count,
                        total_count: bucket.total_count,
                        label: formatDateToUtc(bucket.label, interval),
                    })),
                }));
                combinedData = combinedData.concat(batchCombinedData);
            }

            const combinedByLabel = combinedData.reduce((acc, { value, relatedData }) => {
                relatedData.forEach(({ label, filter_count, total_count }) => {
                    let labelEntry = acc.find((entry) => entry.label === label);
                    if (!labelEntry) {
                        labelEntry = { label, values: [] };
                        acc.push(labelEntry);
                    }
                    labelEntry.values.push({ value, filter_count, total_count });
                });
                return acc;
            }, []);

            const result = calculateNpsScoresByLabel(combinedByLabel);
            setNpsData(result);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching line data:", error);
        }
    }, [selectedOption, conceptFilter, selectedDateName, interval, filters]);


    useEffect(() => {
        setLoading(true);
        const debouncedGetData = debounce(fetchAllLinesData, 1000);
        debouncedGetData();
        return () => {
            debouncedGetData.cancel();
        };
    }, [fetchAllLinesData]);

    if (loading) {
        return (
            <Spin
                className="spinner-by-center"
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }

    const height = filters.length > 0 ? props.widget.layout.h * 39 - 100 : props.widget.layout.h * 39-30;
    return (
        <div style={{ height }}  onMouseDown={(e) => {
            if (e.target.closest(".recharts-brush")) {
                e.stopPropagation();
                e.preventDefault()
            }
        }}>
            <NpsOverTimeScoreChart data={npsData} smoothLine={smoothLine} />
        </div>
    );
};

export default NpsOverTime;

